import { useStores } from '@littlebird/sdk';
import { usePagedLoad } from '@littlebird/ui-utils';
import { observer } from 'mobx-react-lite';
import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { ModeDropdown } from '@/components/blocks/MainLayout/Header/Chat/chat-mode-dropdown';
import { Button, Text, XStack, YStack } from '@/components/ui';
import { Label } from '@/components/ui/Label';
import TextArea from '@/components/ui/TextArea';
import { useEndReachedLoadMore } from '@/hooks/use-end-reached-load-more';
import { navigate } from '@/utils/navigation-service';

import { arenaApis } from './arena.hooks';

const ArenaRoot = observer(function ArenaRoot() {
  const { arenas } = useStores();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { runLoadMore: runLoadMoreArenas, isLoadingMore } = usePagedLoad({
    enabled: true,
    canLoadMore: arenas.hasMorePages,
    loadPage: () => arenas.loadNextPage(),
  });

  const isNearEnd = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return false;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 160;
  }, []);

  const { check: checkEnd } = useEndReachedLoadMore({
    isLoadingMore,
    runLoadMore: runLoadMoreArenas,
    isNearEnd,
  });

  return (
    <YStack
      ref={scrollRef}
      className="h-full w-full max-w-screen-md mx-auto items-center gap-12 pt-12 overflow-y-auto"
      onScroll={checkEnd}
    >
      <YStack className="items-center">
        <Text size="2xl" fontWeight="medium">
          Colosseum
        </Text>
        <Text size="sm" variant="muted">
          Battle it out between agents and see who comes out on top
        </Text>
      </YStack>
      <Form />
      <RecentBattles />
    </YStack>
  );
});

const ModeChip = ({ mode }: { mode: string }) => {
  const unselectMode = arenaApis.useUnselectMode();

  return (
    <XStack className="gap-2 bg-secondary rounded-md py-1 px-2 items-center">
      <Text size="xs" fontFamily="mono">
        {mode}
      </Text>
      <Icons.cancel
        className="size-3 text-foreground-subtle"
        onClick={() => unselectMode(mode)}
      />
    </XStack>
  );
};

const Form = observer(() => {
  const { arenas } = useStores();
  const [question, setQuestion] = useState('');
  const selectedModes = arenaApis.useSelectedModes();
  const resetSelectedModes = arenaApis.useResetSelectedModes;

  const disabled = selectedModes.length === 0 || question.trim() === '';

  return (
    <YStack className="w-full gap-8">
      <ArenaModePopover />
      <YStack className="w-full gap-2">
        <Label>Your Question</Label>
        <TextArea
          className="min-h-24"
          placeholder="What do you want to test?"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
        />
      </YStack>
      <Button
        disabled={disabled}
        onClick={() => {
          try {
            const arena = arenas.createSession({
              agents: selectedModes,
              userMessage: question,
            });
            resetSelectedModes();
            navigate({
              to: '/arena/$arenaId',
              params: {
                arenaId: arena.id,
              },
            });
          } catch (err: any) {
            toast.error('Failed to create session', {
              description: err.message,
              dismissible: true,
            });
          }
        }}
      >
        Start Battle
      </Button>
    </YStack>
  );
});

const ArenaModePopover = () => {
  const selectedModes = arenaApis.useSelectedModes();

  return (
    <YStack className="gap-2">
      <ModeDropdown.Root
        multiple
        selectedModes={selectedModes}
        setSelectedModes={arenaApis.setSelectedMode}
      >
        <ModeDropdown.Trigger size="default" className="justify-between" />
        <ModeDropdown.Content align="start" />
      </ModeDropdown.Root>
      <XStack className="flex-wrap gap-2">
        {selectedModes.map((mode) => (
          <ModeChip key={mode} mode={mode} />
        ))}
      </XStack>
    </YStack>
  );
};

const RecentBattles = observer(() => {
  const { arenas } = useStores();
  const battles = arenas.orderedData || [];

  if (battles.length === 0) return null;

  return (
    <YStack className="w-full gap-4 pb-12">
      <Text size="lg" fontWeight="medium">
        Recent Battles
      </Text>
      <YStack className="gap-2">
        {battles.map((battle) => (
          <XStack
            key={battle.id}
            className="p-4 bg-surface border border-border rounded-lg justify-between items-center hover:bg-surface-hover cursor-pointer transition-colors"
            onClick={() => {
              navigate({
                to: '/arena/$arenaId',
                params: {
                  arenaId: battle.id,
                },
              });
            }}
          >
            <YStack>
              <Text fontWeight="medium" className="capitalize">
                {battle.agents.join(', ')}
              </Text>
            </YStack>
            <Icons.chevronRight className="size-4 text-foreground-muted shrink-0" />
          </XStack>
        ))}
      </YStack>
    </YStack>
  );
});

export default ArenaRoot;
